import { useEffect, useState } from "react";
import { api, dateFmt, formatApiError, money } from "@/lib/api";
import { AlertOctagon, Plus, X } from "lucide-react";
import { toast } from "sonner";

const INC_TYPES = [
    ["percance_vial", "Percance vial"], ["falla_mecanica", "Falla mecánica"], ["accidente", "Accidente"],
    ["lesion_personal", "Lesión de personal"], ["queja_cliente", "Queja de cliente"], ["incidente_clinico", "Incidente clínico"],
    ["incidente_admin", "Incidente administrativo"], ["robo", "Robo"], ["dano_equipo", "Daño a equipo"],
];

export default function Incidents() {
    const [list, setList] = useState([]);
    const [open, setOpen] = useState(false);
    const [units, setUnits] = useState([]);
    const [form, setForm] = useState({
        type: "percance_vial", severity: "media", description: "", location: "", unit_id: "", estimated_cost: 0,
    });

    const load = async () => {
        try {
            const [i, u] = await Promise.all([api.get("/incidents"), api.get("/units")]);
            if (i?.data) setList(i.data);
            if (u?.data) setUnits(u.data);
        } catch (e) { console.warn(e); }
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/incidents", form);
            toast.success("Incidente registrado");
            setOpen(false);
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    return (
        <div className="space-y-4" data-testid="incidents-page">
            <div className="flex justify-between items-center">
                <div className="text-sm text-slate-500">{list.length} incidente(s) registrado(s)</div>
                <button onClick={() => setOpen(true)} className="fm-btn-accent"><Plus className="w-3.5 h-3.5" /> Reportar incidente</button>
            </div>

            <div className="fm-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead><tr className="bg-slate-50 border-b border-slate-200">
                        {["Folio", "Tipo", "Severidad", "Descripción", "Costo est.", "Estatus", "Fecha"].map((h) => (
                            <th key={h} className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                        ))}
                    </tr></thead>
                    <tbody>
                        {list.map((i) => (
                            <tr key={i.id} className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="py-2 px-3 font-mono text-xs">{i.folio}</td>
                                <td className="py-2 px-3 capitalize">{i.type.replace("_", " ")}</td>
                                <td className="py-2 px-3"><span className={`fm-badge ${i.severity === "alta" || i.severity === "critica" ? "fm-badge-critical" : i.severity === "media" ? "fm-badge-warning" : "fm-badge-neutral"}`}>{i.severity}</span></td>
                                <td className="py-2 px-3 max-w-md truncate">{i.description}</td>
                                <td className="py-2 px-3 text-right font-mono">{money(i.estimated_cost || 0)}</td>
                                <td className="py-2 px-3"><span className={`fm-badge ${i.status === "cerrado" ? "fm-badge-positive" : "fm-badge-warning"}`}>{i.status}</span></td>
                                <td className="py-2 px-3 text-xs font-mono text-slate-500">{dateFmt(i.created_at)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {list.length === 0 && <div className="p-10 text-center text-sm text-slate-500"><AlertOctagon className="w-10 h-10 mx-auto mb-2 text-slate-300" strokeWidth={1} />Sin incidentes.</div>}
            </div>

            {open && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-sm max-w-lg w-full">
                        <div className="flex items-center justify-between p-4 border-b border-slate-200">
                            <h3 className="font-display font-bold">Reportar incidente</h3>
                            <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
                        </div>
                        <form onSubmit={submit} className="p-5 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div><label className="fm-label">Tipo</label><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="fm-input">{INC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
                                <div><label className="fm-label">Severidad</label><select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="fm-input"><option value="baja">Baja</option><option value="media">Media</option><option value="alta">Alta</option><option value="critica">Crítica</option></select></div>
                                <div><label className="fm-label">Unidad</label><select value={form.unit_id} onChange={(e) => setForm({ ...form, unit_id: e.target.value })} className="fm-input"><option value="">—</option>{units.map((u) => <option key={u.id} value={u.id}>{u.economic_number}</option>)}</select></div>
                                <div><label className="fm-label">Costo estimado</label><input type="number" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: parseFloat(e.target.value) || 0 })} className="fm-input" /></div>
                                <div className="col-span-2"><label className="fm-label">Descripción *</label><textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="fm-input" rows={3} /></div>
                                <div className="col-span-2"><label className="fm-label">Lugar</label><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="fm-input" /></div>
                            </div>
                            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                                <button type="button" onClick={() => setOpen(false)} className="fm-btn-outline">Cancelar</button>
                                <button type="submit" className="fm-btn-accent">Registrar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
